import React, {useState} from 'react';
import LeftMenu from './Container/LeftMenu/LeftMenu';
import TopBar from './TopBar/TopBar';
import { ChakraProvider, Divider, Flex, Box, Text, Tooltip, Spinner } from '@chakra-ui/react';
import ChatInput from './Container/Chat/ChatInput';
import { Routes, Route } from 'react-router-dom';
import AccountPage from './Pages/AccountPage';
import AccountSettings from './Pages/AccountSettings';
import TeamManagement from './Pages/TeamManagement';
import Integrations from './Pages/Integrations';
import CollaborationForms from './Pages/CollaborationForms';
import HomePage from './Pages/Home';
import ReviewForm from './Pages/ReviewForm/ReviewForm';
import TaskTracker from './Pages/TaskTracker/TaskTracker';
import { WebSocketProvider } from './internal/messaging/WebSocketContext';
import Login from './Login/Login';
import SelectOrganisation from './Login/SelectOrganisation';
import './App.css';
import { IconButton } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import Workspace from './Container/Workspace';
import { motion, AnimatePresence } from 'framer-motion';
import MyLibrary from './Container/MyLibrary/MyLibrary';
import { useWebSocket } from './internal/messaging/WebSocketContext';
import { useParams } from 'react-router-dom';
import EmptyScreen from './Container/Chat/EmptyScreen';
import AnnotationPage from './Pages/annotation/AnnotationPage';

const maxLeftMenuWidth = 268; // Maximum width for the left menu

function Layout({ children, handleClick }) {
  const [leftMenuWidth, setLeftMenuWidth] = useState(() => {
    const percentageWidth = window.innerWidth * 0.16;
    return Math.min(percentageWidth, maxLeftMenuWidth) + "px";
  });

  React.useEffect(() => {
    const handleResize = () => {
      const percentageWidth = window.innerWidth * 0.16;
      setLeftMenuWidth(Math.min(percentageWidth, maxLeftMenuWidth) + "px");
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <ChakraProvider>
      <Flex
        direction="column"
        bg="#F6FAFF"
        height="100vh" // Ensure the layout adapts to the viewport height
        overflow="hidden" // Prevent overflowing content
      >
        {/* Main Content Area */}
        <Flex
          flex="1" // Allow the main content to take up the remaining height
          overflow="hidden" // Prevent overflow
        >
          {/* Left Sidebar */}
          <Box
            w={leftMenuWidth}
            flexShrink={0} // Prevent shrinking
            // borderRight="1px solid #E2E8F0"
            bg="#f6fafe"
            overflow="hidden" // Prevent overflow
          >
            <LeftMenu handleClick={handleClick} setLeftMenuWidth={setLeftMenuWidth} />
          </Box>

          {/* Main Content */}
          <Box flex="1" pl={0} py={0} w={`calc(100% - ${leftMenuWidth})`} maxW={`calc(100% - ${leftMenuWidth})`} h={"100%"}>
            {/* TopBar */}
            <Box height={"60px"}>
              <TopBar />
              {/* <Divider /> */}
            </Box>
            <Box h={`calc(100% - 60px)`} maxH={`calc(100% - 60px)`}>
              {children}
            </Box>
          </Box>
        </Flex>
      </Flex>
    </ChakraProvider>
  );
}

function App() {
  const [isLoading, setIsLoading] = useState(false);
  const handleClick = () => {
    setIsLoading(true);

    // Simulate an async operation (e.g., API call)
    setTimeout(() => {
      setIsLoading(false);
    }, 800); // Hide loader after 2000ms
  };
  return (
    <Routes>
      <Route
        path="/"
        element={
          <WebSocketProvider>
            <Layout handleClick={handleClick}>
              <EmptyScreen isLoading={isLoading} setIsLoading={setIsLoading} flow={"/"} />
            </Layout>
          </WebSocketProvider>
        }
      />
      <Route
        path="/manage-account"
        element={
          <WebSocketProvider>
            <Layout handleClick={handleClick}>
              <AccountPage />
            </Layout>
          </WebSocketProvider>
        }
      />
      <Route
        path="/account-settings"
        element={
          <WebSocketProvider>
            <Layout handleClick={handleClick}>
              <AccountSettings />
            </Layout>
          </WebSocketProvider>
        }
      />
      <Route
        path="/team-management"
        element={
          <WebSocketProvider>
            <Layout handleClick={handleClick}>
              <TeamManagement />
            </Layout>
          </WebSocketProvider>
        }
      />
      <Route
        path="/integrations"
        element={
          <WebSocketProvider>
            <Layout handleClick={handleClick}>
              <Integrations />
            </Layout>
          </WebSocketProvider>
        }
      />
      <Route
        path="/collaboration-forms"
        element={
          <WebSocketProvider>
            <Layout handleClick={handleClick}>
              <CollaborationForms />
            </Layout>
          </WebSocketProvider>
        }
      />
      <Route
        path="/task-tracker"
        element={
          <WebSocketProvider>
            <Layout handleClick={handleClick}>
              <TaskTracker />
            </Layout>
          </WebSocketProvider>
        }
      />
      <Route
        path="/login"
        element={
          <ChakraProvider>
            <Login />
          </ChakraProvider>
        }
      />
      <Route
        path="/select-organisation"
        element={
          <ChakraProvider>
            <SelectOrganisation />
          </ChakraProvider>
        }
      />
      <Route
        path="/my-library"
        element={
          <WebSocketProvider>
            <Layout handleClick={handleClick}>
              <MyLibrary />
            </Layout>
          </WebSocketProvider>
        }
      />
      <Route
        path="/my-library/new-library"
        element={
          <WebSocketProvider>
            <Layout handleClick={handleClick}>
              <MyLibrary />
            </Layout>
          </WebSocketProvider>
        }
      />
      <Route
        path="/my-library/folder/:folderId"
        element={
          <WebSocketProvider>
            <Layout handleClick={handleClick}>
              <MyLibrary />
            </Layout>
          </WebSocketProvider>
        }
      />
      <Route
        path="/my-library/weblinks"
        element={
          <WebSocketProvider>
            <Layout handleClick={handleClick}>
              <MyLibrary />
            </Layout>
          </WebSocketProvider>
        }
      />
      <Route
        path="/f/:formId"
        element={
          <ChakraProvider>
            <ReviewForm />
          </ChakraProvider>
        }
      />
      <Route
        path="/t/:taskId"
        element={
          <WebSocketProvider>
            <Layout handleClick={handleClick}>
              <ChatWindow isLoading={isLoading} setIsLoading={setIsLoading} flow={"chat window"}/>
            </Layout>
          </WebSocketProvider>
        }
      />
      <Route
        path="/annotation/:annotationId"
        element={
          <ChakraProvider>
            <AnnotationPage />
          </ChakraProvider>
        }
      />
    </Routes>
  );
}

function ChatWindow({isLoading, setIsLoading, flow}) {
  const [workspaceOpened, setWorkspaceOpened] = React.useState(true);
  const [showWorkspace, setShowWorkspace] = React.useState(false);

  const { messages } = useWebSocket();
  const [windowOpenedPerTask, setWindowOpenedPerTask] = React.useState(new Map())
  const {taskId} = useParams()
  const { tasks } = useWebSocket();

  const plannerMessage = messages?.find(
    (message) =>
      message.type === 'planner_message' &&
    message.planner_message.port === 'planner'
  );

  React.useEffect(() => {
    setIsLoading(false)
  }, [])
  
  React.useEffect(() => {
    if (plannerMessage && showWorkspace) {
      setWorkspaceOpened(true)
    } else {
      setWorkspaceOpened(false)
    }
    setIsLoading(false)
  }, [plannerMessage, showWorkspace])
  
  const [previousTaskId, setPreviousTaskId] = React.useState(null);
  const [previousMessages, setPreviousMessages] = React.useState(null);

  React.useEffect(() => {
    if (taskId !== previousTaskId) {
      setPreviousTaskId(taskId);
      setPreviousMessages(messages);
    }
  }, [taskId, messages]);

  if (!messages || (taskId === previousTaskId && messages === previousMessages)) {
    return (
      <Flex width={'100%'} height="72vh" justifyContent={'center'} alignItems={"center"} py={20}>
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="blue.500"
          size="lg"
        />
      </Flex>
    );
  }
  
  const isFirstTimeWorkspace = () => {
    console.log("isFirstTimeWorkspace ",windowOpenedPerTask)
    const returnValue = taskId ? (
      // windowOpenedPerTask ? (
        windowOpenedPerTask.has(taskId) ? false : true 
      // ) : false
      ) : false
    
      windowOpenedPerTask.set(taskId, true)

    return returnValue
  }

  return (
    <Flex h="100%" gap={0} maxW={"100%"}>
      {/* Chat Section */}
      <Box flex="2" borderRadius="md" maxW={"100%"}>
        <ChatInput showWorkspace={showWorkspace} workspaceOpened={workspaceOpened}/>
      </Box>
      {
        plannerMessage ? (
          <>
          {/* Vertical Divider */}
          <Box
            position="relative"
            w="0px" // Thin vertical line
            bg="#e5e5e5" // Subtle gray color
            mx={0} // Add a small margin between sections
            mr={workspaceOpened ? 0 : 6}
          >
            <Box
              position="absolute"
              top="25%"
              left="50%"
              transform="translate(-50%, -50%)"
              bg="white"
              borderRadius="50%"
              p={0} // Remove extra padding
              boxShadow="sm"
              border="1px solid #E2E8F0"
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="25px" // Ensure width is equal
              h="25px" // Ensure height is equal
              cursor={'pointer'}
              onClick={() => setShowWorkspace((prevState) => !prevState)}
            >
              {/* <IconButton
                icon={showWorkspace ? KeyboardArrowRightIcon : KeyboardArrowLeftIcon}
                color="grey"
                size="sm"
              /> */}

              <Tooltip
                label= {showWorkspace ? "Hide Workspace" : "Show Workspace"}
                placement={showWorkspace ? "left" : "right"}
                bg={"#4d4d4d"}
                color={"#f2f2f2"}
                borderRadius="3%"
                fontWeight={500}
                fontFamily="'Inter', sans-serif" 
                hasArrow
              >
                <IconButton sx={{padding:0.5, "&:hover":{backgroundColor: "transparent"}}}>
                  {showWorkspace ? 
                    <KeyboardArrowRightIcon sx={{ padding:0, fontSize: 24, color: "#5d5d5d" }}/> : 
                    <KeyboardArrowLeftIcon sx={{ padding:0, fontSize: 24, color: "#5d5d5d" }}/>}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          {
            // showWorkspace ? (
                <Workspace 
                  showWorkspace={showWorkspace} 
                  setShowWorkspace={setShowWorkspace}
                  workspaceOpened={workspaceOpened}
                  setWorkspaceOpened={setWorkspaceOpened}
                  isFirstTimeWorkspace={isFirstTimeWorkspace}
                />
            // ) : null
          }
          </>
        ) : null
      }
    </Flex>
  )
}

export default App;
